import LegalPageLayout, { type LegalSection } from '../components/layout/LegalPageLayout';
import Seo from '../components/Seo';
import '../styles/legal-pages.css';

const sections: LegalSection[] = [
  { id: 'general', title: '1. Общие положения' },
  { id: 'terms', title: '2. Термины' },
  { id: 'subject', title: '3. Предмет оферты' },
  { id: 'accept', title: '4. Акцепт и заключение договора' },
  { id: 'price', title: '5. Стоимость и тарифы' },
  { id: 'payment', title: '6. Порядок оплаты' },
  { id: 'rights', title: '7. Права и обязанности сторон' },
  { id: 'refund', title: '8. Возвраты' },
  { id: 'liability', title: '9. Ответственность' },
  { id: 'force', title: '10. Форс‑мажор' },
  { id: 'disputes', title: '11. Споры' },
  { id: 'final', title: '12. Реквизиты и заключительные положения' },
];

export default function OfferPage() {
  return (
    <LegalPageLayout
      title="Публичная оферта"
      updatedAt="28 января 2026 г."
      version="1.0"
      sections={sections}
    >
      <Seo
        title="Публичная оферта — Recvisit"
        description="Публичная оферта на оказание услуг доступа к SaaS‑сервису Recvisit: условия, оплата, ответственность."
        canonicalPath="/offer"
      />
      <section id="general" className="legal-section">
        <h2>1. Общие положения</h2>
        <p>
          Настоящий документ является официальным предложением (публичной офертой) Индивидуального предпринимателя
          Шмелевой Екатерины Владимировны (далее — <strong>Исполнитель</strong>) заключить договор на оказание услуг по
          предоставлению доступа к сервису <strong>Recvisit</strong> (далее — <strong>Услуги</strong>).
        </p>
        <p>
          <strong>Важно:</strong> Услуги предоставляются исключительно юридическим лицам и индивидуальным предпринимателям, 
          зарегистрированным в соответствии с законодательством Российской Федерации. Физические лица, не являющиеся 
          индивидуальными предпринимателями, не могут быть Заказчиками и не имеют права акцептовать настоящую оферту.
        </p>
        <p>
          В соответствии со статьей 437 Гражданского кодекса РФ, настоящий документ является публичной офертой. Акцепт
          оферты означает заключение договора на условиях, изложенных в настоящей оферте.
        </p>
      </section>

      <section id="terms" className="legal-section">
        <h2>2. Термины</h2>
        <ul>
          <li><strong>Оферта</strong> — настоящий документ, размещенный на сайте.</li>
          <li><strong>Акцепт</strong> — полное и безоговорочное принятие условий оферты путем совершения действий, указанных в разделе 4.</li>
          <li><strong>Заказчик</strong> — лицо, осуществившее акцепт оферты.</li>
          <li><strong>Сервис</strong> — программное обеспечение Recvisit, предоставляемое по модели SaaS.</li>
          <li><strong>Тариф</strong> — условия использования Сервиса и стоимость Услуг.</li>
        </ul>
      </section>

      <section id="subject" className="legal-section">
        <h2>3. Предмет оферты</h2>
        <p>
          Исполнитель предоставляет Заказчику доступ к Сервису Recvisit на условиях выбранного тарифа, а Заказчик
          обязуется оплатить Услуги.
        </p>
        <p>
          Сервис предоставляется по модели SaaS. Исполнитель обеспечивает функционирование Сервиса на своей
          инфраструктуре, а Заказчик использует Сервис удаленно через сеть Интернет.
        </p>
      </section>

      <section id="accept" className="legal-section">
        <h2>4. Акцепт и заключение договора</h2>
        <p>Договор считается заключенным с момента совершения Заказчиком следующих действий:</p>
        <ul>
          <li>регистрация/авторизация в системе (в том числе на домене <strong>reg.recvi.ru</strong>);</li>
          <li>выбор тарифа (если применимо);</li>
          <li>оплата Услуг (если Услуги предоставляются на платной основе).</li>
        </ul>
        <p>
          Совершая акцепт, Заказчик подтверждает ознакомление и согласие с настоящей офертой, Пользовательским
          соглашением и Политикой конфиденциальности.
        </p>
      </section>

      <section id="price" className="legal-section">
        <h2>5. Стоимость и тарифы</h2>
        <p>
          Актуальные тарифы, стоимость, состав услуг и ограничения публикуются на сайте и/или в интерфейсе Сервиса.
          Стоимость указывается в рублях Российской Федерации.
        </p>
        <p>
          Исполнитель вправе изменять стоимость тарифов, уведомляя Заказчика заблаговременно (в том числе путем
          публикации новой редакции условий). Изменения не распространяются на уже оплаченные периоды.
        </p>
      </section>

      <section id="payment" className="legal-section">
        <h2>6. Порядок оплаты</h2>
        <ul>
          <li>Оплата производится на условиях предоплаты, если иное не согласовано сторонами отдельно.</li>
          <li>Моментом оплаты считается поступление денежных средств на счет Исполнителя/подтверждение платежа.</li>
          <li>Комиссии платежных систем (если применимо) не подлежат возврату.</li>
        </ul>
      </section>

      <section id="rights" className="legal-section">
        <h2>7. Права и обязанности сторон</h2>
        <p>
          Права и обязанности сторон определяются настоящей офертой, Пользовательским соглашением и иными документами,
          размещенными на сайте/в интерфейсе Сервиса.
        </p>
        <p>
          Заказчик обязуется использовать Сервис законно, не нарушать работу Сервиса, обеспечивать сохранность учетных
          данных и соблюдать требования законодательства РФ, включая законодательство о персональных данных.
        </p>
      </section>

      <section id="refund" className="legal-section">
        <h2>8. Возвраты</h2>
        <p>
          Условия возврата денежных средств (если применимо) определяются Политикой возврата и/или условиями тарифа.
        </p>
        <p>
          По общему правилу возврат за неиспользованный остаток оплаченного периода не производится, если иное прямо не
          предусмотрено условиями тарифа или законом.
        </p>
      </section>

      <section id="liability" className="legal-section">
        <h2>9. Ответственность</h2>
        <p>
          Сервис предоставляется по принципу «как есть» (<em>as is</em>). Исполнитель не несет ответственности за
          упущенную выгоду и косвенные убытки Заказчика.
        </p>
        <p>
          Исполнитель не несет ответственности за косвенные убытки, упущенную выгоду, потерю данных, коммерческие потери 
          и иные убытки, не связанные напрямую с нарушением обязательств Исполнителя по предоставлению доступа к Сервису.
        </p>
        <p>
          Ответственность Исполнителя по любым требованиям Заказчика ограничивается суммой оплаты за последний оплаченный
          период использования Сервиса.
        </p>
        <p>
          Исполнитель не несет ответственности перед третьими лицами (включая сотрудников Клиента) за обработку 
          персональных данных и иные действия Клиента. Клиент обязуется самостоятельно урегулировать все претензии третьих 
          лиц, связанные с использованием Сервиса Клиентом.
        </p>
        <p>
          Заказчик несет полную ответственность за законность обработки и передачу персональных данных сотрудников, за
          наличие правовых оснований и необходимых согласий, а также за действия, совершенные с использованием его
          учетной записи.
        </p>
      </section>

      <section id="force" className="legal-section">
        <h2>10. Форс‑мажор</h2>
        <p>
          Стороны освобождаются от ответственности за неисполнение обязательств при наличии обстоятельств непреодолимой
          силы, включая стихийные бедствия, военные действия, решения органов власти, массовые кибератаки, сбои
          магистральных провайдеров связи и иные обстоятельства, не зависящие от сторон.
        </p>
      </section>

      <section id="disputes" className="legal-section">
        <h2>11. Споры</h2>
        <p>
          Претензионный порядок обязателен. Срок рассмотрения претензии — до 30 календарных дней. При недостижении
          согласия спор подлежит рассмотрению в суде по месту жительства Исполнителя (г. Липецк, Липецкая область), если
          иное не предусмотрено императивными нормами.
        </p>
      </section>

      <section id="final" className="legal-section">
        <h2>12. Реквизиты и заключительные положения</h2>
        <p>
          Исполнитель вправе изменять условия оферты путем публикации новой редакции. Продолжение использования Сервиса
          после публикации изменений означает согласие Заказчика с новой редакцией.
        </p>
        <h3>Реквизиты Исполнителя</h3>
        <div className="legal-box">
          <p><strong>Индивидуальный предприниматель Шмелева Екатерина Владимировна</strong></p>
          <p>ИНН: 482306497385</p>
          <p>ОГРНИП: 324480000041882</p>
          <p>Адрес: Липецкая область, г. Липецк, ул. Советская, д. 4, оф. 619</p>
          <p>Email: <a href="mailto:info@recvi.ru">info@recvi.ru</a></p>
          <p>Сайт: <a href="https://recvi.ru" target="_blank" rel="noopener noreferrer">recvi.ru</a></p>
        </div>
      </section>
    </LegalPageLayout>
  );
}

